// src/components/SectionTabs.jsx
// Sections inside a main tab, each with its own address, e.g.
//   /portal/users/activity   /portal/control-panel/billing
// sections: [{ id, label, icon, group? }] (only the ones this person may
// open); with groups (3.18.5; 3.18.6: groups: [{ name, icon, about }]) the
// groups are the main tabs; the open group's title and `about` (3.18.7),
// then its sections as a smaller row, as in Users → Activity log;
// aliases: { oldSlug: sectionId } for older addresses; the address is then
// tidied to the current one. children(sectionId) renders the section.
import { useEffect, useState } from 'react';
import { pathForTab } from '../utils/tabRoutes';
import { useAuth } from '../hooks/useAuth';
import { logTabOpened } from '../utils/api';
import { trackPage } from '../utils/visits';
import { TAB, TAB_ON, TAB_ROW, PILL, PILL_ON, PILL_ROW, SECTION_TITLE, SECTION_SUB, PAGE_TITLE } from '../theme/tabs';
import Emo, { emoLabel } from './Emo';

const BASE = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');

// The section named by the address: the segment after the tab's own slug
// (or after any slug, for older addresses such as /control-panel/activity)
function sectionFromLocation(sections, aliases) {
  const path = window.location.pathname.slice(BASE.length).replace(/^\/+|\/+$/g, '');
  const parts = path.split('/');
  const slug = parts[1] || (aliases[parts[0]] ? parts[0] : '');
  const id = aliases[slug] || slug;
  return sections.some(s => s.id === id) ? id : null;
}

export default function SectionTabs({ tabId, title, about = null, sections, aliases = {}, groups = null, children }) {
  const [picked, setPicked] = useState(() => sectionFromLocation(sections, aliases));
  const sub = sections.some(s => s.id === picked) ? picked : sections[0]?.id;
  const base = pathForTab(tabId);
  const { getToken } = useAuth();

  // Activity log: which section was opened
  useEffect(() => { if (sub) { logTabOpened(`${tabId}/${sub}`, getToken); trackPage(`${tabId}/${sub}`, getToken); } }, [tabId, sub, getToken]);   // 3.49.0: and Analytics

  // Older addresses and the tab's bare address show the current one
  useEffect(() => {
    if (!sub) return;
    const want = `${base}/${sub}`;
    if (window.location.pathname !== want && !window.location.search) window.history.replaceState({ tab: tabId, sub }, '', want);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);

  // Browser Back / Forward between sections
  useEffect(() => {
    const onPop = () => setPicked(sectionFromLocation(sections, aliases));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function open(id) {
    setPicked(id);
    const path = `${base}/${id}`;
    if (window.location.pathname !== path) window.history.pushState({ tab: tabId, sub: id }, '', path);
  }

  // A tab that opens a section (plain clicks stay in the page; Cmd/Ctrl-click
  // opens a new browser tab)
  const tabLink = (id, on, style, onStyle, content, key = id) => (
    <a key={key} href={`${base}/${id}`} role="tab" aria-selected={on}
      onClick={e => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        open(id);
      }}
      style={{ ...style, ...(on ? onStyle : {}) }}>
      {content}
    </a>
  );
  const plainTitle = String(title || '').replace(/^\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic})*\s*/u, '');   // no icon in the screen-reader label
  const grouped = !!groups && sections.some(t => t.group);
  const openGroup = grouped ? sections.find(t => t.id === sub)?.group : null;

  return (
    <div>
      <h2 style={S.pageTitle} className="cp-title">{emoLabel(title)}</h2>
      {/* 3.49.0: a line about the page, under its title */}
      {about && <p style={{ ...SECTION_SUB, marginTop: -6, marginBottom: 14 }}>{about}</p>}
      {grouped && (
        <>
          <div style={S.subTabs} role="tablist" aria-label={`${plainTitle} sections`}>
            {groups.filter(g => sections.some(t => t.group === g.name)).map(g => {
              const first = sections.find(t => t.group === g.name);
              return tabLink(first.id, openGroup === g.name, S.subTab, S.subTabActive,
                <><Emo e={g.icon} /> {g.name}</>, `group:${g.name}`);
            })}
          </div>
          {(() => { const g = groups.find(x => x.name === openGroup); return g && (
            <div style={{ marginBottom: 12 }}>
              <h3 style={SECTION_TITLE}>{g.name}</h3>
              {g.about && <p style={SECTION_SUB}>{g.about}</p>}
            </div>
          ); })()}
          {sections.filter(t => t.group === openGroup).length > 1 && (
            <div style={S.innerTabs} role="tablist" aria-label={openGroup}>
              {sections.filter(t => t.group === openGroup).map(t =>
                tabLink(t.id, sub === t.id, S.innerTab, S.innerTabActive, <><Emo e={t.icon} /> {t.label}</>))}
            </div>
          )}
        </>
      )}
      {sections.length > 1 && !grouped && (
        <div style={S.subTabs} role="tablist" aria-label={`${plainTitle} sections`}>
          {sections.map(t => (
            <a key={t.id} href={`${base}/${t.id}`} role="tab" aria-selected={sub === t.id}
              onClick={e => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                open(t.id);
              }}
              style={{ ...S.subTab, ...(sub === t.id ? S.subTabActive : {}) }}>
              <Emo e={t.icon} /> {t.label}
            </a>
          ))}
        </div>
      )}
      {sub && children(sub)}
    </div>
  );
}

const S = {
  pageTitle   : PAGE_TITLE,
  subTabs       : TAB_ROW,
  subTab        : TAB,
  subTabActive  : TAB_ON,
  // The open group's sections: the smaller pills (theme/tabs.js)
  innerTabs     : { ...PILL_ROW, margin: '0 0 20px' },
  innerTab      : PILL,
  innerTabActive: PILL_ON,
};
